import React, { useEffect, useState } from "react";
import SearchDropdown from "../applicationUi/components/SearchDropdown";
import SingleSelect from "../applicationUi/components/SingleSelect";
import { useDispatch, useSelector } from "react-redux";
import {
  createAppointment,
  fetchAppointmentsByClinicianId,
  checkForConflicts,
  updateAppointment,
  fetchAppointmentsByClinicianIdAllClinics,
} from "../store/thunks/appointmentsThunk";
import { appendAppointmentToBlock } from "../store/thunks/blocksThunk";
import { fetchClinicianById } from "../store/thunks/cliniciansThunk";
import { fetchClinicResources } from "../store/thunks/resourceThunk";
import { checkResourceAvailability } from "../store/thunks/resourceScheduleThunk";
import QuickAddClientModal from "./QuickAddClientModal";
import ConfirmAppointmentModal from "./ConfirmAppointmentModal";
import { format } from "date-fns";
import { Dialog } from "@headlessui/react";
import {
  XMarkIcon,
  UserIcon,
  CalendarIcon,
  ClockIcon,
  HomeIcon,
  BuildingOfficeIcon,
} from "@heroicons/react/24/outline";
import { toast } from 'react-toastify';

const QuickAddAppointmentModal = ({
  isOpen,
  onClose,
  initialDate,
  initialTime,
  initialClinicianFname,
  initialClinicianLname,
  selectedClinician: initialSelectedClinician,
  clinic,
  mode = "create",
  appointment = null,
  blockId = null,
}) => {
  const dispatch = useDispatch();
  const user = useSelector((state) => state.auth.user);
  const selectedClinicFromRedux = useSelector((state) => state.clinics.selectedClinic);
  const allClinics = useSelector((state) => state.clinics.clinics) || [];
  const availableResources =
    useSelector((state) => state.resources.resources) || [];
  
  // State for clinic selection when "All Clinics" is selected
  const [selectedClinicForAppointment, setSelectedClinicForAppointment] = useState(null);

  // Helper function to get the clinic ID to use for appointments
  const getClinicIdForAppointment = () => {
    return selectedClinicForAppointment?._id || clinic?._id || selectedClinician?.defaultLocation;
  };

  const [isNewClient, setIsNewClient] = useState(false);
  const [selectedClient, setSelectedClient] = useState(null);
  const [selectedService, setSelectedService] = useState(null);
  const [selectedReferral, setSelectedReferral] = useState(null);
  const [isBlock, setIsBlock] = useState(false);
  const [blockDates, setBlockDates] = useState([]);
  const conflicts = useSelector((state) => state.appointments.conflicts) || [];

  const [isRecurring, setIsRecurring] = useState(false);
  const [recurrenceInterval, setRecurrenceInterval] = useState(7); // Default weekly
  const [recurringDates, setRecurringDates] = useState([]); // To store calculated recurring dates
  const [isVirtual, setIsVirtual] = useState(false);

  const [showActiveOnly, setShowActiveOnly] = useState(true);
  const [totalSessions, setTotalSessions] = useState(10); // Add this line

  const [selectedClinician, setSelectedClinician] = useState(null);
  const [selectedRoom, setSelectedRoom] = useState(null);

  const [selectedResources, setSelectedResources] = useState([]);
  const [resourceTypeFilter, setResourceTypeFilter] = useState("");

  const [modalContent, setModalContent] = useState({
    date: initialDate || null,
    time: initialTime || "",
    clinicianFname: initialClinicianFname || "",
    clinicianLname: initialClinicianLname || "",
  });

  const [resourceAvailability, setResourceAvailability] = useState({});
  
  // Confirmation modal state
  const [showConfirmModal, setShowConfirmModal] = useState(false);
  const [pendingAppointmentData, setPendingAppointmentData] = useState(null);
  const [availabilityWarning, setAvailabilityWarning] = useState("");

  // Helper function to convert time string to minutes for comparison
  const timeToMinutes = (timeStr) => {
    if (!timeStr) return null;
    const [hours, minutes] = timeStr.split(':').map(Number);
    return hours * 60 + minutes;
  };

  // Helper function to check if appointment is within clinic hours
  const checkClinicHours = (appointmentDate, appointmentTime, selectedClinic) => {
    console.log(`[QuickAddAppointmentModal] Checking clinic hours for ${appointmentDate} at ${appointmentTime}`);
    console.log(`[QuickAddAppointmentModal] selectedClinic:`, selectedClinic);
    console.log(`[QuickAddAppointmentModal] selectedClinic.hoursOfOperation:`, selectedClinic?.hoursOfOperation);
    
    if (!selectedClinic?.hoursOfOperation || !appointmentDate || !appointmentTime) {
      console.log(`[QuickAddAppointmentModal] Missing clinic hoursOfOperation data - using default hours check`);
      
      // Temporary fallback for testing - assume clinic closes at 6 PM
      if (appointmentTime && appointmentDate) {
        const appointmentMinutes = timeToMinutes(appointmentTime);
        const dayOfWeek = new Intl.DateTimeFormat('en-US', { weekday: 'long' }).format(appointmentDate);
        
        // Default clinic hours: 8 AM - 6 PM (480 minutes to 1080 minutes)
        if (appointmentMinutes < 480 || appointmentMinutes >= 1080) {
          return {
            isWithinHours: false,
            warning: `The clinic is closed at this time. Standard operating hours: 8:00 AM - 6:00 PM.`
          };
        }
      }
      
      return { isWithinHours: true, warning: null };
    }

    const dayOfWeek = new Intl.DateTimeFormat('en-US', { weekday: 'long' }).format(appointmentDate);
    const dayHours = selectedClinic.hoursOfOperation[dayOfWeek];
    console.log(`[QuickAddAppointmentModal] Day: ${dayOfWeek}, Hours:`, dayHours);
    
    if (!dayHours?.open || !dayHours?.close) {
      console.log(`[QuickAddAppointmentModal] Clinic closed on ${dayOfWeek}`);
      return {
        isWithinHours: false,
        warning: `The clinic is closed on ${dayOfWeek}s.`
      };
    }

    const appointmentMinutes = timeToMinutes(appointmentTime);
    const openMinutes = timeToMinutes(dayHours.open);
    const closeMinutes = timeToMinutes(dayHours.close);
    console.log(`[QuickAddAppointmentModal] Time check - Appointment: ${appointmentMinutes}, Open: ${openMinutes}, Close: ${closeMinutes}`);

    if (appointmentMinutes < openMinutes || appointmentMinutes >= closeMinutes) {
      console.log(`[QuickAddAppointmentModal] Appointment outside clinic hours`);
      return {
        isWithinHours: false,
        warning: `The clinic is closed at this time. Operating hours on ${dayOfWeek}s: ${dayHours.open} - ${dayHours.close}.`
      };
    }

    console.log(`[QuickAddAppointmentModal] Clinic hours check passed`);
    return { isWithinHours: true, warning: null };
  };

  // Helper function to check if appointment is within clinician availability
  const checkClinicianAvailability = (appointmentDate, appointmentTime, selectedClinicianData, selectedClinic) => {
    console.log(`[QuickAddAppointmentModal] Checking clinician availability for ${appointmentDate} at ${appointmentTime}`);
    
    if (!selectedClinicianData?.availability || !appointmentDate || !appointmentTime) {
      return { isAvailable: true, warning: null };
    }

    const dayOfWeek = new Intl.DateTimeFormat('en-US', { weekday: 'long' }).format(appointmentDate);
    const clinicId = selectedClinic?._id || clinic?._id;

    // Find matching availability period
    const matchingAvailability = selectedClinicianData.availability.find(avail => {
      const startDate = new Date(avail.startDate);
      startDate.setHours(0, 0, 0, 0);
      
      const endDate = new Date(avail.endDate);
      endDate.setHours(23, 59, 59, 999);
      
      const appointmentDateCopy = new Date(appointmentDate);
      appointmentDateCopy.setHours(12, 0, 0, 0);
      
      const isInRange = appointmentDateCopy >= startDate && appointmentDateCopy <= endDate;
      const isMatchingClinic = avail.clinic?.toString() === clinicId?.toString();

      return isInRange && isMatchingClinic;
    });

    if (!matchingAvailability) {
      const clinicName = selectedClinic?.name || clinic?.name || 'this clinic';
      return {
        isAvailable: false,
        warning: `${selectedClinicianData.firstName} ${selectedClinicianData.lastName} is not available at ${clinicName} during this time period.`
      };
    }

    // Check specific day schedule
    const daySchedule = matchingAvailability.schedule?.[dayOfWeek];
    if (!daySchedule || daySchedule.length === 0) {
      return {
        isAvailable: false,
        warning: `${selectedClinicianData.firstName} ${selectedClinicianData.lastName} is not scheduled to work on ${dayOfWeek}s.`
      };
    }

    // Check if time falls within any available slot
    const appointmentMinutes = timeToMinutes(appointmentTime);
    const isWithinSlot = daySchedule.some(slot => {
      if (!slot.start || !slot.end) return false;
      const startMinutes = timeToMinutes(slot.start);
      const endMinutes = timeToMinutes(slot.end);
      return appointmentMinutes >= startMinutes && appointmentMinutes < endMinutes;
    });

    if (!isWithinSlot) {
      const availableSlots = daySchedule
        .filter(slot => slot.start && slot.end)
        .map(slot => `${slot.start}-${slot.end}`)
        .join(', ');
      
      const availableTimesText = availableSlots ? `Available times: ${availableSlots}.` : 'No available times scheduled.';
      
      return {
        isAvailable: false,
        warning: `${selectedClinicianData.firstName} ${selectedClinicianData.lastName} is not available at ${appointmentTime} on ${dayOfWeek}s. ${availableTimesText}`
      };
    }

    return { isAvailable: true, warning: null };
  };

  // Helper function to check for cross-clinic appointment conflicts  
  const checkCrossClinicConflicts = async (appointmentDate, appointmentTime, duration, selectedClinicianData, currentClinicId) => {
    console.log(`[QuickAddAppointmentModal] Checking cross-clinic conflicts for ${appointmentDate} at ${appointmentTime}`);
    
    if (!selectedClinicianData?._id || !appointmentDate || !appointmentTime || !duration) {
      return { hasConflicts: false, warning: null };
    }

    try {
      // Fetch all appointments for this clinician across all clinics
      console.log(`[QuickAddAppointmentModal] Dispatching fetchAppointmentsByClinicianIdAllClinics for clinician:`, selectedClinicianData._id);
      const response = await dispatch(fetchAppointmentsByClinicianIdAllClinics(selectedClinicianData._id));
      console.log(`[QuickAddAppointmentModal] API response:`, response);
      const allAppointments = response.payload || [];
      
      console.log(`[QuickAddAppointmentModal] Found ${allAppointments.length} existing appointments to check:`, allAppointments);

      // Calculate new appointment time range
      const [hours, minutes] = appointmentTime.split(':').map(Number);
      const newStart = new Date(appointmentDate);
      newStart.setHours(hours, minutes, 0, 0);
      const newEnd = new Date(newStart);
      newEnd.setMinutes(newStart.getMinutes() + duration);

      const conflicts = [];
      const buffer = 45 * 60 * 1000; // 45 minutes in milliseconds

      allAppointments.forEach(appointment => {
        // Skip appointments at the current clinic (already handled by other checks)
        if (appointment.clinic._id === currentClinicId) return;
        
        // Skip cancelled appointments
        if (appointment.status && appointment.status.some(s => 
          s.toLowerCase().includes('cancelled') || s.toLowerCase().includes('no show')
        )) return;

        const existingStart = new Date(appointment.start);
        const existingEnd = new Date(appointment.end);

        // Check for direct overlap
        const hasDirectOverlap = newStart < existingEnd && newEnd > existingStart;

        // Check for buffer zone conflicts (45 minutes)
        const newStartWithBuffer = new Date(newStart.getTime() - buffer);
        const newEndWithBuffer = new Date(newEnd.getTime() + buffer);
        const hasBufferConflict = !hasDirectOverlap && (
          (existingEnd > newStartWithBuffer && existingEnd <= newStart) || // Existing ends within buffer before new
          (existingStart >= newEnd && existingStart < newEndWithBuffer)    // Existing starts within buffer after new
        );

        if (hasDirectOverlap || hasBufferConflict) {
          conflicts.push({
            appointment,
            type: hasDirectOverlap ? 'direct' : 'buffer',
            clinic: appointment.clinic.name,
            client: `${appointment.client.firstName} ${appointment.client.lastName}`,
            time: `${existingStart.toLocaleTimeString([], {hour: '2-digit', minute:'2-digit'})} - ${existingEnd.toLocaleTimeString([], {hour: '2-digit', minute:'2-digit'})}`
          });
        }
      });

      if (conflicts.length > 0) {
        const directConflicts = conflicts.filter(c => c.type === 'direct');
        const bufferConflicts = conflicts.filter(c => c.type === 'buffer');
        
        let warningMessage = '';
        
        if (directConflicts.length > 0) {
          const conflictDescriptions = directConflicts.map(c => 
            `${c.client} at ${c.clinic} (${c.time})`
          ).join(', ');
          warningMessage += `${selectedClinicianData.firstName} ${selectedClinicianData.lastName} has a conflicting appointment: ${conflictDescriptions}.`;
        }
        
        if (bufferConflicts.length > 0) {
          const bufferDescriptions = bufferConflicts.map(c => 
            `${c.client} at ${c.clinic} (${c.time})`
          ).join(', ');
          if (warningMessage) warningMessage += ' ';
          warningMessage += `Additionally, there is an appointment within 45 minutes: ${bufferDescriptions}.`;
        }

        return { hasConflicts: true, warning: warningMessage };
      }

      return { hasConflicts: false, warning: null };
    } catch (error) {
      console.error(`[QuickAddAppointmentModal] Error checking cross-clinic conflicts:`, error);
      return { hasConflicts: false, warning: null };
    }
  };

  // Initialize clinic selection based on current context
  useEffect(() => {
    if (selectedClinicFromRedux?._id === "all") {
      // If "All Clinics" is selected, don't set a default clinic - user must choose
      setSelectedClinicForAppointment(null);
    } else if (clinic && clinic._id !== "all") {
      // Use the passed clinic if it's a valid clinic
      setSelectedClinicForAppointment(clinic);
    } else if (selectedClinician?.defaultLocation) {
      // Fallback to clinician's default location
      const clinicianClinic = allClinics.find(c => c._id === selectedClinician.defaultLocation);
      setSelectedClinicForAppointment(clinicianClinic || null);
    }
  }, [selectedClinicFromRedux, clinic, selectedClinician, allClinics]);

  useEffect(() => {
    // Use clinic prop first, fallback to clinician's default location
    const clinicToUse = selectedClinicForAppointment || clinic;
    const clinicId = clinicToUse?._id || 
                     initialSelectedClinician?.defaultLocation ||
                     selectedClinician?.defaultLocation;
    
    if (clinicId && clinicId !== "all") {
      dispatch(fetchClinicResources(clinicId));
    }
  }, [dispatch, clinic, initialSelectedClinician, selectedClinician, selectedClinicForAppointment]);

  useEffect(() => {
    if (mode === "edit" && appointment) {
      const start = new Date(appointment.start);
      const hours = start.getHours().toString().padStart(2, "0");
      const minutes = start.getMinutes().toString().padStart(2, "0");

      setModalContent({
        date: start,
        time: `${hours}:${minutes}`,
        clinicianFname: appointment.clinician.firstName,
        clinicianLname: appointment.clinician.lastName,
      });

      setSelectedClient(appointment.client);
      setSelectedService({
        ...appointment.service,
        duration: appointment.service?.duration ?? 30,
      });
      setSelectedClinician(appointment.clinician);
      setIsVirtual(appointment.isVirtual || false);
    }
  }, [mode, appointment]);

  useEffect(() => {
    if (
      mode === "create" &&
      initialSelectedClinician &&
      initialSelectedClinician._id
    ) {
      dispatch(fetchClinicianById(initialSelectedClinician._id)).then(
        (response) => {
          setSelectedClinician(response.payload);
        }
      );
    } else if (mode === "edit" && appointment) {
      setSelectedClinician(appointment.clinician);
    }
  }, [initialSelectedClinician, mode, appointment, dispatch]);

  useEffect(() => {
    const handleKeyDown = (event) => {
      if (event.key === "Escape") {
        onClose();
      }
    };
    document.addEventListener("keydown", handleKeyDown);
    return () => document.removeEventListener("keydown", handleKeyDown);
  }, [onClose]);

  useEffect(() => {
    if (
      isBlock &&
      modalContent.date &&
      modalContent.time &&
      totalSessions > 0 &&
      selectedClinician
    ) {
      try {
        const [hours, minutes] = modalContent.time.split(":").map(Number);
        const dates = Array.from({ length: totalSessions }, (_, i) => {
          const date = new Date(modalContent.date);
          date.setDate(date.getDate() + i * 7); // Default weekly for blocks
          date.setHours(hours);
          date.setMinutes(minutes);
          return date;
        });

        setBlockDates(dates); // Save block dates for preview

        const clinicianId = selectedClinician._id;
        dispatch(checkForConflicts({ dates, clinicianId }))
          .then((action) =>
            console.log("Conflicts for blocks:", action.payload)
          )
          .catch((error) =>
            console.error("Failed to fetch block conflicts:", error)
          );
      } catch (error) {
        console.error("Error calculating block dates:", error);
      }
    } else {
      setBlockDates([]); // Clear block dates if conditions are not met
    }
  }, [
    isBlock,
    modalContent.date,
    modalContent.time,
    totalSessions,
    selectedClinician,
    dispatch,
  ]);

  useEffect(() => {
    if (
      isRecurring &&
      modalContent.date &&
      modalContent.time &&
      totalSessions > 0 &&
      selectedClinician
    ) {
      try {
        const [hours, minutes] = modalContent.time.split(":").map(Number);
        const dates = Array.from({ length: totalSessions }, (_, i) => {
          const date = new Date(modalContent.date);
          date.setDate(date.getDate() + i * recurrenceInterval); // Use recurrenceInterval for recurring
          date.setHours(hours);
          date.setMinutes(minutes);
          return date;
        });

        setRecurringDates(dates); // Save recurring dates for preview

        const clinicianId = selectedClinician._id;
        dispatch(checkForConflicts({ dates, clinicianId }))
          .then((action) =>
            console.log("Conflicts for recurring:", action.payload)
          )
          .catch((error) =>
            console.error("Failed to fetch recurring conflicts:", error)
          );
      } catch (error) {
        console.error("Error calculating recurring dates:", error);
      }
    } else {
      setRecurringDates([]); // Clear recurring dates if conditions are not met
    }
  }, [
    isRecurring,
    modalContent.date,
    modalContent.time,
    totalSessions,
    recurrenceInterval,
    selectedClinician,
    dispatch,
  ]);

  // useEffect(() => {
  //   // Log blockId for debugging
  //   if (blockId) {
  //     console.log("QuickAddAppointmentModal received blockId:", blockId);
  //   }
  // }, [blockId]);

  useEffect(() => {
    if (
      modalContent.date &&
      modalContent.time &&
      selectedService?.duration &&
      selectedClinician?.defaultLocation
    ) {
      const [hours, minutes] = modalContent.time.split(":").map(Number);
      const start = new Date(modalContent.date);
      start.setHours(hours);
      start.setMinutes(minutes);

      const clinicId = getClinicIdForAppointment();
      
      dispatch(
        checkResourceAvailability({
          clinicId: clinicId,
          start: start.toISOString(),
          duration: selectedService.duration,
        })
      ).then((action) => {
        // Store the availability response in a more easily accessible format
        const availabilityMap = {};
        action.payload.forEach((resource) => {
          availabilityMap[resource.resourceId] = {
            bookingStatus: resource.bookingStatus,
            overlappingBookings: resource.overlappingBookings,
          };
        });
        setResourceAvailability(availabilityMap);
      });
    }
  }, [
    modalContent.date,
    modalContent.time,
    selectedService,
    selectedClinician,
    appointment,
    dispatch,
  ]);

  const handleServiceSelection = (service) => {
    setSelectedService(service);
  };

  const handleReferralSelection = (referral) => {
    setSelectedReferral(referral);
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setModalContent((prevContent) => ({
      ...prevContent,
      [name]: value,
    }));
  };

  // Handle date changes from the date input
  const handleDateChange = (e) => {
    const newDateStr = e.target.value; // "YYYY-MM-DD"
    if (newDateStr) {
      const [year, month, day] = newDateStr.split("-");
      const updatedDate = new Date(modalContent.date || Date.now());
      updatedDate.setFullYear(
        parseInt(year),
        parseInt(month) - 1,
        parseInt(day)
      );
      setModalContent((prev) => ({ ...prev, date: updatedDate }));
    }
  };

  const handleAppendToBlock = (appointmentData) => {
    dispatch(
      appendAppointmentToBlock({
        blockId,
        appointmentData,
      })
    )
      .unwrap()
      .then(() => {
        onClose();
        console.log('[QuickAddAppointmentModal] Dispatching success toast: Appointment successfully added to block!');
        toast.success("Appointment successfully added to block!");
      })
      .catch((err) => {
        console.error("Failed to append appointment to block:", err);
        console.log('[QuickAddAppointmentModal] Dispatching error toast: Failed to append appointment');
        toast.error("Failed to append appointment. Check console for details.");
      });
  };

  const handleSubmit = (event) => {
    if (isRecurring) {
      handleRecurringSave(event);
    } else {
      handleSave(event);
    }
  };

  const handleSave = async (event) => {
    event.preventDefault();
    const { date, time } = modalContent;

    if (!time) {
      console.error("Time is not defined");
      return;
    }

    const missingFields = [];
    if (!selectedClient) missingFields.push("Client");
    if (!selectedService) missingFields.push("Service");
    if (!selectedClinician) missingFields.push("Clinician");
    
    // Check if clinic is required and missing (when "All Clinics" is selected)
    if (selectedClinicFromRedux?._id === "all" && !selectedClinicForAppointment) {
      missingFields.push("Clinic");
    }
    
    if (missingFields.length > 0) {
      console.log('[QuickAddAppointmentModal] Dispatching warning toast: Missing required fields');
      toast.warning(`Please select the following required fields: ${missingFields.join(", ")}`);
      return;
    }

    // Always run all checks to collect all warnings
    // Use clinic prop first, fallback to Redux selectedClinic
    const clinicToCheck = clinic || selectedClinicFromRedux;
    console.log(`[QuickAddAppointmentModal] clinic prop:`, clinic);
    console.log(`[QuickAddAppointmentModal] selectedClinicFromRedux:`, selectedClinicFromRedux);
    console.log(`[QuickAddAppointmentModal] clinicToCheck:`, clinicToCheck);
    
    const clinicHoursCheck = checkClinicHours(date, time, clinicToCheck);
    const clinicianAvailabilityCheck = checkClinicianAvailability(date, time, selectedClinician, clinicToCheck);
    
    // Check for cross-clinic conflicts (async)
    const crossClinicCheck = await checkCrossClinicConflicts(
      date, 
      time, 
      selectedService?.duration || 30, 
      selectedClinician, 
      clinicToCheck?._id
    );

    console.log(`[QuickAddAppointmentModal] Clinic hours result:`, clinicHoursCheck);
    console.log(`[QuickAddAppointmentModal] Clinician availability result:`, clinicianAvailabilityCheck);
    console.log(`[QuickAddAppointmentModal] Cross-clinic conflicts result:`, crossClinicCheck);

    // Collect any warnings that occurred
    const allWarnings = [];
    if (!clinicHoursCheck.isWithinHours && clinicHoursCheck.warning) {
      console.log(`[QuickAddAppointmentModal] Adding clinic hours warning:`, clinicHoursCheck.warning);
      allWarnings.push(clinicHoursCheck.warning);
    }
    if (!clinicianAvailabilityCheck.isAvailable && clinicianAvailabilityCheck.warning) {
      console.log(`[QuickAddAppointmentModal] Adding clinician availability warning:`, clinicianAvailabilityCheck.warning);
      allWarnings.push(clinicianAvailabilityCheck.warning);
    }
    if (crossClinicCheck.hasConflicts && crossClinicCheck.warning) {
      console.log(`[QuickAddAppointmentModal] Adding cross-clinic conflict warning:`, crossClinicCheck.warning);
      allWarnings.push(crossClinicCheck.warning);
    }

    console.log(`[QuickAddAppointmentModal] All warnings collected:`, allWarnings);

    // If any checks failed, show confirmation modal
    if (allWarnings.length > 0) {
      const finalWarning = allWarnings.join(' Additionally, ');
      console.log(`[QuickAddAppointmentModal] Final warning message:`, finalWarning);
      setAvailabilityWarning(finalWarning);
      setPendingAppointmentData({ date, time });
      setShowConfirmModal(true);
      return;
    }

    // If all checks pass, proceed with normal save
    proceedWithSave();
  };

  const proceedWithSave = () => {
    const { date, time } = modalContent;
    const [hours, minutes] = time.split(":").map(Number);
    const start = new Date(date);
    start.setHours(hours, minutes, 0, 0);
    const end = new Date(start);
    end.setMinutes(start.getMinutes() + (selectedService.duration || 30));

    const appointmentData = {
      clinic: getClinicIdForAppointment(),
      clinician: selectedClinician._id,
      client: selectedClient._id,
      title: selectedService.description,
      start: start.toISOString(),
      end: end.toISOString(),
      service: selectedService._id,
      status: mode === "create" ? ["Booked"] : appointment?.status,
      history:
        mode === "create"
          ? [
              {
                status: "Booked",
                comments: "",
                timestamp: new Date().toISOString(),
                user: user._id,
              },
            ]
          : [
              ...appointment.history,
              {
                status: "Changed",
                comments: "Appointment changed",
                user: user._id,
                timestamp: new Date().toISOString(),
              },
            ],
      userId: user._id,
      isVirtual,
    };

    // Only include resource fields in create mode
    if (mode === "create") {
      appointmentData.resource = selectedRoom?._id;
      appointmentData.resources = selectedResources.map((r) => r._id);
    }

    // Add isBlock and totalSessions if mode is create and isBlock is true
    if (mode === "create" && isBlock) {
      appointmentData.isBlock = true;
      appointmentData.totalSessions = totalSessions;
    }

    if (blockId) {
      // Append appointment to block if blockId is provided
      dispatch(appendAppointmentToBlock({ blockId, appointmentData }))
        .unwrap()
        .then(() => {
          console.log('[QuickAddAppointmentModal] Dispatching success toast: Appointment successfully added to block!');
        toast.success("Appointment successfully added to block!");
          onClose();
        })
        .catch((err) => {
          console.error("Failed to append appointment to block:", err);
          toast.error("Failed to append appointment to block.");
        });
    } else if (mode === "create") {
      // Create new appointment or block
      dispatch(createAppointment(appointmentData))
        .unwrap()
        .then(() => {
          console.log('[QuickAddAppointmentModal] Dispatching success toast: Appointment successfully created!');
          toast.success("Appointment successfully created!");
          onClose();
        })
        .catch((err) => {
          console.error("Failed to create appointment:", err);
          toast.error("Failed to create appointment.");
        });
    } else if (mode === "edit" && appointment) {
      // Update existing appointment logic
      dispatch(
        updateAppointment({ id: appointment._id, updatedData: appointmentData })
      )
        .unwrap()
        .then(() => {
          console.log('[QuickAddAppointmentModal] Dispatching success toast: Appointment successfully updated!');
          toast.success("Appointment successfully updated!");
          onClose();
        })
        .catch((err) => {
          console.error("Failed to update appointment:", err);
          toast.error("Failed to update appointment.");
        });
    }
  };

  // Handle confirmation modal actions
  const handleConfirmSave = () => {
    setShowConfirmModal(false);
    if (isRecurring) {
      proceedWithRecurringSave();
    } else {
      proceedWithSave();
    }
  };

  const handleCancelConfirm = () => {
    setShowConfirmModal(false);
    setPendingAppointmentData(null);
    setAvailabilityWarning("");
  };

  const handleRecurringSave = async (event) => {
    event.preventDefault();
    const { date, time } = modalContent;

    if (!time) {
      console.error("Time is not defined");
      return;
    }

    const missingFields = [];
    if (!selectedClient) missingFields.push("Client");
    if (!selectedService) missingFields.push("Service");
    if (!selectedClinician) missingFields.push("Clinician");
    
    // Check if clinic is required and missing (when "All Clinics" is selected)
    if (selectedClinicFromRedux?._id === "all" && !selectedClinicForAppointment) {
      missingFields.push("Clinic");
    }
    
    if (missingFields.length > 0) {
      console.log('[QuickAddAppointmentModal] Dispatching warning toast: Missing required fields');
      toast.warning(`Please select the following required fields: ${missingFields.join(", ")}`);
      return;
    }

    // Always run all checks to collect all warnings for first appointment (applies to all)
    // Use clinic prop first, fallback to Redux selectedClinic
    const clinicToCheck = clinic || selectedClinicFromRedux;
    const clinicHoursCheck = checkClinicHours(date, time, clinicToCheck);
    const clinicianAvailabilityCheck = checkClinicianAvailability(date, time, selectedClinician, clinicToCheck);
    
    // Check for cross-clinic conflicts (async)
    const crossClinicCheck = await checkCrossClinicConflicts(
      date, 
      time, 
      selectedService?.duration || 30, 
      selectedClinician, 
      clinicToCheck?._id
    );

    // Collect any warnings that occurred
    const allWarnings = [];
    if (!clinicHoursCheck.isWithinHours && clinicHoursCheck.warning) {
      allWarnings.push(clinicHoursCheck.warning);
    }
    if (!clinicianAvailabilityCheck.isAvailable && clinicianAvailabilityCheck.warning) {
      allWarnings.push(clinicianAvailabilityCheck.warning);
    }
    if (crossClinicCheck.hasConflicts && crossClinicCheck.warning) {
      allWarnings.push(crossClinicCheck.warning);
    }

    // If any checks failed, show confirmation modal
    if (allWarnings.length > 0) {
      const combinedWarnings = allWarnings.join(' Additionally, ');
      const finalWarning = `${combinedWarnings} This warning applies to all recurring appointments.`;

      setAvailabilityWarning(finalWarning);
      setPendingAppointmentData({ date, time });
      setShowConfirmModal(true);
      return;
    }

    // If all checks pass, proceed with normal recurring save
    proceedWithRecurringSave();
  };

  const proceedWithRecurringSave = () => {
    const { date, time } = modalContent;
    const [hours, minutes] = time.split(":").map(Number);
    const baseStartDate = new Date(date);
    baseStartDate.setHours(hours, minutes, 0, 0);

    const baseEndDate = new Date(baseStartDate);
    baseEndDate.setMinutes(
      baseStartDate.getMinutes() + (selectedService.duration || 30)
    );

    // Compute recurring dates
    const interval = isRecurring ? recurrenceInterval : 7; // Default to weekly if no interval
    const recurringDates = [];
    for (let i = 0; i < totalSessions; i++) {
      const start = new Date(baseStartDate);
      start.setDate(start.getDate() + i * interval);

      const end = new Date(start);
      end.setMinutes(start.getMinutes() + (selectedService.duration || 30));

      recurringDates.push({ start, end });
    }

    // Dispatch the `createAppointment` thunk for each recurring date
    recurringDates.forEach(({ start, end }) => {
      const updatedData = {
        clinic: getClinicIdForAppointment(),
        clinician: selectedClinician._id,
        client: selectedClient._id,
        title: selectedService.description,
        start: start.toISOString(),
        end: end.toISOString(),
        service: selectedService._id,
        status: ["Booked"],
        history: [
          {
            status: "Booked",
            comments: "Booked as recurring appointment",
            timestamp: new Date().toISOString(),
            user: user._id,
          },
        ],
        userId: user._id,
        isVirtual,
      };

      dispatch(createAppointment(updatedData));
    });

    onClose();
  };

  const handleResourceSelection = (resource) => {
    const availability = resourceAvailability[resource._id];
    if (!availability || availability.bookingStatus !== "Available") return;

    // Check if already selected
    const isAlreadySelected = selectedResources.some(
      (r) => r._id === resource._id
    );

    if (isAlreadySelected) {
      // Remove from selected resources
      setSelectedResources((prev) =>
        prev.filter((r) => r._id !== resource._id)
      );
    } else {
      // Add to selected resources
      setSelectedResources((prev) => [...prev, resource]);
    }
  };

  if (!isOpen) return null;

  // Convert date to YYYY-MM-DD for input
  const dateInputValue =
    modalContent.date instanceof Date && !isNaN(modalContent.date)
      ? modalContent.date.toISOString().slice(0, 10)
      : "";

  return (
    <Dialog
      open={isOpen}
      onClose={onClose}
      className="fixed inset-0 z-60 overflow-y-auto">
      <div className="min-h-screen px-4 text-center">
        <Dialog.Overlay className="fixed inset-0 bg-gray-900/50 transition-opacity" />

        {/* Centering trick */}
        <span className="inline-block h-screen align-middle" aria-hidden="true">
          &#8203;
        </span>

        <div data-component="QuickAddAppointmentModal" className="inline-block w-full max-w-4xl my-8 overflow-hidden text-left align-middle transition-all transform bg-white shadow-xl rounded-sm">
          <div className="absolute right-4 top-4">
            <button
              type="button"
              className="rounded-sm bg-white text-gray-400 hover:text-gray-500 focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:ring-offset-2"
              onClick={onClose}>
              <span className="sr-only">Close</span>
              <XMarkIcon className="h-6 w-6" aria-hidden="true" />
            </button>
          </div>

          <div className="bg-white px-6 py-6">
            <form onSubmit={handleSubmit}>
              {/* Title */}
              <Dialog.Title
                as="h3"
                className="text-xl font-semibold leading-6 text-gray-900 mb-6">
                {mode === "edit"
                  ? "Update Appointment"
                  : "Create New Appointment"}
              </Dialog.Title>

              <div className="space-y-6">
                {/* Client Information Section */}
                <div className="space-y-6 border-b border-gray-900/10 pb-6">
                  <div className="flex items-center space-x-2 mb-1.5">
                    <UserIcon className="h-5 w-5 text-gray-500" />
                    <h3 className="text-base font-semibold text-gray-900">
                      Client Information
                    </h3>
                  </div>
                  <p className="mt-1 text-sm leading-6 text-gray-600">
                    {mode === "edit"
                      ? "Search for a different client or keep the existing client selected."
                      : "Search for an existing client or create a new one."}
                  </p>

                  {selectedClient && selectedClient.phoneNumbers && (
                    <div className="bg-gray-50 p-4 rounded-sm">
                      <h4 className="text-sm font-medium text-gray-900 mb-2">
                        Selected Client
                      </h4>
                      <p className="text-sm text-gray-600">
                        {selectedClient.firstName} {selectedClient.lastName},{" "}
                        {selectedClient.email},{" "}
                        {selectedClient.phoneNumbers
                          .map(
                            (phone) =>
                              `${phone.type}: ${phone.number} ${phone.note}`
                          )
                          .join(", ")}
                      </p>
                    </div>
                  )}

                  <div className="space-y-4">
                    <SearchDropdown
                      prefix=":client:"
                      displayFields={[
                        "firstName",
                        "lastName",
                        "email",
                        "phoneNumbers",
                      ]}
                      onSelection={(client) => setSelectedClient(client)}
                      placeholder="Search for a client"
                      value={selectedClient}
                      className="w-full"
                    />
                    {mode === "create" && (
                      <button
                        type="button"
                        onClick={() => setIsNewClient(true)}
                        className="rounded-sm bg-indigo-600 px-3 py-2 text-sm font-semibold text-white shadow-sm hover:bg-indigo-500 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-indigo-600">
                        Add a New Client
                      </button>
                    )}
                  </div>
                </div>

                {/* Appointment Information Section */}
                <div className="space-y-6 border-b border-gray-900/10 pb-6">
                  <div className="flex items-center space-x-2 mb-1.5">
                    <CalendarIcon className="h-5 w-5 text-gray-500" />
                    <h3 className="text-base font-semibold text-gray-900">
                      Appointment Information
                    </h3>
                  </div>
                  <p className="mt-1 text-sm leading-6 text-gray-600">
                    {mode === "edit"
                      ? "You can now change the date, time, clinician, and service."
                      : "Date, Time, and Clinician are prepopulated from your selection."}
                  </p>

                  {/* Date and Time */}
                  <div className="sm:col-span-6 grid grid-cols-1 sm:grid-cols-2 gap-4">
                    <div>
                      <div className="flex items-center space-x-2 mb-1.5">
                        <CalendarIcon className="h-5 w-5 text-gray-500" />
                        <label className="text-sm font-medium text-gray-900">
                          Date
                        </label>
                      </div>
                      <input
                        type="date"
                        value={dateInputValue}
                        onChange={handleDateChange}
                        className="block w-full rounded-sm border-0 py-1.5 px-3 text-gray-900 shadow-sm ring-1 ring-inset ring-gray-300 placeholder:text-gray-400 focus:ring-2 focus:ring-inset focus:ring-indigo-600 sm:text-sm"
                      />
                    </div>
                    <div>
                      <div className="flex items-center space-x-2 mb-1.5">
                        <ClockIcon className="h-5 w-5 text-gray-500" />
                        <label className="text-sm font-medium text-gray-900">
                          Time
                        </label>
                      </div>
                      <input
                        type="time"
                        name="time"
                        value={modalContent.time || ""}
                        onChange={handleInputChange}
                        className="block w-full rounded-sm border-0 py-1.5 px-3 text-gray-900 shadow-sm ring-1 ring-inset ring-gray-300 placeholder:text-gray-400 focus:ring-2 focus:ring-inset focus:ring-indigo-600 sm:text-sm"
                      />
                    </div>
                  </div>

                  {/* Clinic */}
                  <div>
                    <div className="flex items-center space-x-2 mb-1.5">
                      <BuildingOfficeIcon className="h-5 w-5 text-gray-500" />
                      <label className="text-sm font-medium text-gray-900">
                        Clinic
                        {selectedClinicFromRedux?._id === "all" && <span className="text-red-500">*</span>}
                      </label>
                    </div>
                    
                    {selectedClinicFromRedux?._id === "all" ? (
                      /* Show clinic selector when "All Clinics" is selected */
                      <SingleSelect
                        options={allClinics.map((clinic) => ({
                          value: clinic._id,
                          label: clinic.name,
                        }))}
                        selectedValue={
                          selectedClinicForAppointment
                            ? { 
                                value: selectedClinicForAppointment._id, 
                                label: selectedClinicForAppointment.name 
                              }
                            : null
                        }
                        onChange={(selected) => {
                          if (selected) {
                            const clinic = allClinics.find(c => c._id === selected.value);
                            setSelectedClinicForAppointment(clinic);
                          }
                        }}
                        placeholder="Select a clinic..."
                        className="w-full"
                      />
                    ) : (
                      /* Show clinic info when a specific clinic is selected */
                      clinic?.name && (
                        <div className="bg-gray-50 p-3 rounded-sm">
                          <p className="text-sm text-gray-900 font-medium">
                            {clinic.name}
                          </p>
                          {clinic.address && (
                            <p className="text-sm text-gray-600 mt-1">
                              {clinic.address.street}, {clinic.address.city}
                            </p>
                          )}
                        </div>
                      )
                    )}
                  </div>

                  {/* Clinician */}
                  <div>
                    <div className="flex items-center space-x-2 mb-1.5">
                      <UserIcon className="h-5 w-5 text-gray-500" />
                      <label className="text-sm font-medium text-gray-900">
                        Clinician
                      </label>
                    </div>
                    {selectedClinician && selectedClinician.specialization && (
                      <div className="bg-gray-50 p-3 rounded-sm mb-2">
                        <p className="text-sm text-gray-600">
                          {selectedClinician.firstName}{" "}
                          {selectedClinician.lastName}
                          {selectedClinician.specialization.length > 0 && (
                            <>, {selectedClinician.specialization.join(", ")}</>
                          )}
                        </p>
                      </div>
                    )}
                    <SearchDropdown
                      prefix=":user:"
                      displayFields={["firstName", "lastName", "email"]}
                      onSelection={(clinician) => {
                        // Fetch full clinician details including defaultLocation
                        dispatch(fetchClinicianById(clinician._id)).then((response) => {
                          setSelectedClinician(response.payload);
                          setModalContent((prev) => ({
                            ...prev,
                            clinicianFname: response.payload.firstName,
                            clinicianLname: response.payload.lastName,
                          }));
                        });
                      }}
                      placeholder="Search for a clinician"
                      className="w-full"
                    />
                  </div>

                  {/* Service and Duration */}
                  <div>
                    <div className="flex items-center space-x-2 mb-1.5">
                      <ClockIcon className="h-5 w-5 text-gray-500" />
                      <label className="text-sm font-medium text-gray-900">
                        Service
                      </label>
                    </div>
                    {selectedService && (
                      <div className="bg-gray-50 p-3 rounded-sm mb-2">
                        <p className="text-sm text-gray-600">
                          {selectedService.description}, $
                          {typeof selectedService.price === "number"
                            ? selectedService.price.toFixed(2)
                            : "N/A"}
                        </p>
                      </div>
                    )}
                    <div className="flex items-center gap-4">
                      <div className="flex-1 w-full">
                        <SearchDropdown
                          prefix=":service:"
                          displayFields={[
                            "serviceCode",
                            "description",
                            "price",
                          ]}
                          onSelection={handleServiceSelection}
                          placeholder="Search for a service by name or service code"
                          activeServices={showActiveOnly}
                          className="w-full"
                        />
                      </div>
                      <div className="flex items-center">
                        <input
                          id="showActiveOnly"
                          type="checkbox"
                          checked={showActiveOnly}
                          onChange={() => setShowActiveOnly(!showActiveOnly)}
                          className="h-4 w-4 rounded border-gray-300 text-indigo-600 focus:ring-indigo-600"
                        />
                        <label
                          htmlFor="showActiveOnly"
                          className="ml-2 text-sm text-gray-900">
                          Show only active services
                        </label>
                      </div>
                    </div>

                    {selectedService && (
                      <div className="mt-4">
                        <label className="text-sm font-medium text-gray-900">
                          Duration
                        </label>
                        <select
                          value={selectedService?.duration ?? 30}
                          onChange={(e) =>
                            setSelectedService({
                              ...selectedService,
                              duration: parseInt(e.target.value),
                            })
                          }
                          disabled={!selectedService}
                          className="mt-1 block w-full rounded-sm border-0 py-1.5 px-3 text-gray-900 shadow-sm ring-1 ring-inset ring-gray-300 focus:ring-2 focus:ring-inset focus:ring-indigo-600 sm:text-sm">
                          {[
                            15, 30, 45, 60, 75, 90, 105, 120, 135, 150, 165,
                            180, 195, 210, 225, 240,
                          ].map((minutes) => (
                            <option key={minutes} value={minutes}>
                              {Math.floor(minutes / 60)}h {minutes % 60}m
                            </option>
                          ))}
                        </select>
                        {selectedService.duration === 0 && (
                          <p className="mt-2 text-sm text-red-600">
                            Please select a duration for this service.
                          </p>
                        )}
                      </div>
                    )}
                  </div>
                </div>

                {/* Resources Section - Only show in create mode */}
                {mode !== "edit" && (
                  <div className="space-y-6 border-b border-gray-900/10 pb-6">
                    <div className="flex items-center space-x-2 mb-1.5">
                      <HomeIcon className="h-5 w-5 text-gray-500" />
                      <h3 className="text-base font-semibold text-gray-900">
                        Resources
                      </h3>
                    </div>
                    {modalContent.time && selectedService ? (
                      <div>
                        <div className="flex justify-between items-center mb-3">
                          <p className="text-sm text-gray-600">
                            Select available resources for this appointment
                          </p>
                          <select
                            value={resourceTypeFilter}
                            onChange={(e) =>
                              setResourceTypeFilter(e.target.value)
                            }
                            className="rounded-sm border-0 py-1.5 pl-3 pr-10 text-gray-900 ring-1 ring-inset ring-gray-300 focus:ring-2 focus:ring-indigo-600 sm:text-sm">
                            <option value="">All Resources</option>
                            <option value="Room">Rooms</option>
                            <option value="Equipment">Equipment</option>
                            <option value="Testing Material">
                              Testing Materials
                            </option>
                            <option value="Assistive Device">
                              Assistive Devices
                            </option>
                            <option value="Other">Other</option>
                          </select>
                        </div>
                        <div className="space-y-4">
                          {Object.entries(
                            availableResources
                              .filter((resource) =>
                                resourceTypeFilter
                                  ? resource.type === resourceTypeFilter &&
                                    resource.status === "active"
                                  : resource.status === "active"
                              )
                              .reduce((acc, resource) => {
                                if (!acc[resource.type])
                                  acc[resource.type] = [];
                                acc[resource.type].push(resource);
                                return acc;
                              }, {})
                          ).map(([type, resources]) => (
                            <div
                              key={type}
                              className="bg-gray-50 p-3 rounded-sm">
                              <h4 className="text-sm font-medium text-gray-900 mb-2">
                                {type}
                              </h4>
                              <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
                                {resources.map((resource) => {
                                  const availability =
                                    resourceAvailability[resource._id];
                                  const overlappingBookings =
                                    availability?.overlappingBookings || [];
                                  const bookingTimes =
                                    overlappingBookings.length > 0
                                      ? overlappingBookings
                                          .map(
                                            (booking) =>
                                              `${format(
                                                new Date(booking.start),
                                                "h:mm a"
                                              )} - ${format(
                                                new Date(booking.end),
                                                "h:mm a"
                                              )}`
                                          )
                                          .join(", ")
                                      : null;

                                  return (
                                    <div
                                      key={resource._id}
                                      className="py-2 flex items-center">
                                      <input
                                        id={`resource-${resource._id}`}
                                        name={`resource-${resource._id}`}
                                        type="checkbox"
                                        checked={selectedResources.some(
                                          (r) => r._id === resource._id
                                        )}
                                        onChange={() =>
                                          handleResourceSelection(resource)
                                        }
                                        disabled={
                                          !availability ||
                                          availability.bookingStatus !==
                                            "Available"
                                        }
                                        className={`h-4 w-4 rounded border-gray-300 text-indigo-600 focus:ring-indigo-600 ${
                                          !availability ||
                                          availability.bookingStatus !==
                                            "Available"
                                            ? "opacity-50 cursor-not-allowed"
                                            : ""
                                        }`}
                                      />
                                      <label
                                        htmlFor={`resource-${resource._id}`}
                                        className="ml-3 block text-sm leading-6 text-gray-900">
                                        {resource.name}
                                        {resource.description &&
                                          ` - ${resource.description}`}
                                        {resource.location &&
                                          ` (${resource.location})`}
                                        <span
                                          className={`ml-2 ${
                                            !availability
                                              ? "text-gray-400"
                                              : availability.bookingStatus ===
                                                "Available"
                                              ? "text-green-600"
                                              : availability.bookingStatus ===
                                                "Unavailable"
                                              ? "text-orange-600"
                                              : "text-red-600"
                                          }`}>
                                          (
                                          {availability
                                            ? availability.bookingStatus
                                            : "Loading..."}
                                          )
                                        </span>
                                        {availability?.bookingStatus ===
                                          "Unavailable" &&
                                          bookingTimes && (
                                            <div className="text-xs text-gray-500 mt-1">
                                              Unavailable: {bookingTimes}
                                            </div>
                                          )}
                                      </label>
                                    </div>
                                  );
                                })}
                              </div>
                            </div>
                          ))}
                        </div>
                      </div>
                    ) : (
                      <p className="text-sm text-gray-500">
                        Please select an appointment time and service first to
                        view available resources.
                      </p>
                    )}
                  </div>
                )}

                {/* Booking Options Section */}
                <div className="space-y-6">
                  <div className="flex items-center space-x-2 mb-1.5">
                    <CalendarIcon className="h-5 w-5 text-gray-500" />
                    <h3 className="text-base font-semibold text-gray-900">
                      Booking Options
                    </h3>
                  </div>

                  <div className="flex flex-col sm:flex-row sm:items-center gap-4">
                    <div className="flex items-center">
                      <input
                        id="isBlock"
                        name="isBlock"
                        type="checkbox"
                        checked={isBlock}
                        onChange={() => {
                          setIsBlock(!isBlock);
                          if (!isBlock) setIsRecurring(false);
                        }}
                        className="h-4 w-4 rounded border-gray-300 text-indigo-600 focus:ring-indigo-600"
                      />
                      <label
                        htmlFor="isBlock"
                        className="ml-2 text-sm text-gray-900">
                        Block Appointment
                      </label>
                    </div>
                    <div className="flex items-center">
                      <input
                        id="isRecurring"
                        name="isRecurring"
                        type="checkbox"
                        checked={isRecurring}
                        onChange={() => {
                          setIsRecurring(!isRecurring);
                          if (!isRecurring) setIsBlock(false);
                        }}
                        className="h-4 w-4 rounded border-gray-300 text-indigo-600 focus:ring-indigo-600"
                      />
                      <label
                        htmlFor="isRecurring"
                        className="ml-2 text-sm text-gray-900">
                        Recurring Appointment
                      </label>
                    </div>
                    <div className="flex items-center">
                      <input
                        id="isVirtual"
                        name="isVirtual"
                        type="checkbox"
                        checked={isVirtual}
                        onChange={() => setIsVirtual(!isVirtual)}
                        className="h-4 w-4 rounded border-gray-300 text-indigo-600 focus:ring-indigo-600"
                      />
                      <label
                        htmlFor="isVirtual"
                        className="ml-2 text-sm text-gray-900">
                        Virtual Appointment
                      </label>
                    </div>
                    <div className="flex items-center">
                      <label className="text-sm font-medium text-gray-900 mr-2">
                        Total Sessions:
                      </label>
                      <input
                        type="number"
                        min="1"
                        value={totalSessions}
                        onChange={(e) =>
                          setTotalSessions(parseInt(e.target.value, 10))
                        }
                        className="block w-20 rounded-sm border-0 py-1.5 px-3 text-gray-900 shadow-sm ring-1 ring-inset ring-gray-300 focus:ring-2 focus:ring-inset focus:ring-indigo-600 sm:text-sm"
                      />
                    </div>
                    {isRecurring && recurringDates.length > 0 && (
                      <div className="flex items-center">
                        <label className="text-sm font-medium text-gray-900 mr-2">
                          Repeat every
                        </label>
                        <input
                          type="number"
                          min="1"
                          value={recurrenceInterval}
                          onChange={(e) =>
                            setRecurrenceInterval(parseInt(e.target.value, 10))
                          }
                          className="block w-20 rounded-sm border-0 py-1.5 px-3 text-gray-900 shadow-sm ring-1 ring-inset ring-gray-300 focus:ring-2 focus:ring-inset focus:ring-indigo-600 sm:text-sm"
                        />
                        <span className="ml-2 text-sm text-gray-900">days</span>
                      </div>
                    )}
                  </div>

                  {/* Virtual Appointment URL Display */}
                  {isVirtual && selectedClinician && (
                    <div className="mt-3">
                      {selectedClinician.virtualAppointmentUrl ? (
                        <div className="rounded-md bg-blue-50 p-3 border border-blue-200">
                          <div className="flex items-center justify-between">
                            <div className="flex items-center min-w-0">
                              <svg className="h-5 w-5 text-blue-400 flex-shrink-0" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor">
                                <path d="M2 6a2 2 0 012-2h6a2 2 0 012 2v8a2 2 0 01-2 2H4a2 2 0 01-2-2V6zm12.553 1.106A1 1 0 0014 8v4a1 1 0 00.553.894l2 1A1 1 0 0018 13V7a1 1 0 00-1.447-.894l-2 1z" />
                              </svg>
                              <div className="ml-2 min-w-0">
                                <p className="text-xs font-medium text-blue-800">Virtual Meeting Link</p>
                                <a
                                  href={selectedClinician.virtualAppointmentUrl}
                                  target="_blank"
                                  rel="noopener noreferrer"
                                  className="text-sm text-blue-600 hover:text-blue-800 underline truncate block"
                                >
                                  {selectedClinician.virtualAppointmentUrl}
                                </a>
                              </div>
                            </div>
                            <button
                              type="button"
                              onClick={() => {
                                navigator.clipboard.writeText(selectedClinician.virtualAppointmentUrl);
                                toast.success("Link copied to clipboard");
                              }}
                              className="ml-2 flex-shrink-0 rounded-md bg-blue-100 px-2 py-1 text-xs font-medium text-blue-700 hover:bg-blue-200"
                            >
                              Copy
                            </button>
                          </div>
                        </div>
                      ) : (
                        <div className="rounded-md bg-yellow-50 p-3 border border-yellow-200">
                          <div className="flex items-center">
                            <svg className="h-5 w-5 text-yellow-400 flex-shrink-0" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor">
                              <path fillRule="evenodd" d="M8.485 2.495c.673-1.167 2.357-1.167 3.03 0l6.28 10.875c.673 1.167-.17 2.625-1.516 2.625H3.72c-1.347 0-2.189-1.458-1.515-2.625L8.485 2.495zM10 6a.75.75 0 01.75.75v3.5a.75.75 0 01-1.5 0v-3.5A.75.75 0 0110 6zm0 9a1 1 0 100-2 1 1 0 000 2z" clipRule="evenodd" />
                            </svg>
                            <p className="ml-2 text-sm text-yellow-700">
                              No virtual appointment URL configured for this clinician. Please add one in HR Info.
                            </p>
                          </div>
                        </div>
                      )}
                    </div>
                  )}

                  {/* Block Grid */}
                  {isBlock && blockDates.length > 0 && (
                    <div className="mt-4">
                      <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
                        {blockDates.map((date, index) => (
                          <div
                            key={index}
                            className="p-4 border rounded-sm bg-gray-50">
                            <p className="text-sm font-medium text-gray-900">
                              Block #{index + 1}
                            </p>
                            <p className="text-sm text-gray-600">
                              {new Date(date).toDateString()} at{" "}
                              {new Date(date).toLocaleTimeString([], {
                                hour: "2-digit",
                                minute: "2-digit",
                                hour12: true,
                              })}
                            </p>
                            {index === 6 && (
                              <p className="text-xs text-gray-600 mt-1">
                                (Progress Report)
                              </p>
                            )}
                            {conflicts[index] && (
                              <p className="mt-2 text-sm">
                                <span
                                  className={`font-semibold ${
                                    conflicts[index].status === "No conflict"
                                      ? "text-green-600"
                                      : "text-red-600"
                                  }`}>
                                  {conflicts[index].status}
                                </span>
                              </p>
                            )}
                          </div>
                        ))}
                      </div>
                      {(() => {
                        const totalConflicts = conflicts.reduce(
                          (sum, conflict) =>
                            sum +
                            conflict.conflictDetails.appointments +
                            conflict.conflictDetails.meetings,
                          0
                        );
                        return totalConflicts > 0 ? (
                          <p className="mt-4 text-sm text-red-600 text-center">
                            {totalConflicts} conflicting appointments/meetings
                            detected. Booking is still permitted.
                          </p>
                        ) : (
                          <p className="mt-4 text-sm text-green-600 text-center">
                            No conflicts detected. Booking is permitted.
                          </p>
                        );
                      })()}
                    </div>
                  )}

                  {/* Recurring Grid */}
                  {isRecurring && recurringDates.length > 0 && (
                    <div className="mt-4">
                      <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
                        {recurringDates.map((date, index) => (
                          <div
                            key={index}
                            className="p-4 border rounded-sm bg-gray-50">
                            <p className="text-sm font-medium text-gray-900">
                              Appointment #{index + 1}
                            </p>
                            <p className="text-sm text-gray-600">
                              {new Date(date).toDateString()} at{" "}
                              {new Date(date).toLocaleTimeString([], {
                                hour: "2-digit",
                                minute: "2-digit",
                                hour12: true,
                              })}
                            </p>
                            {conflicts[index] && (
                              <p className="mt-2 text-sm">
                                <span
                                  className={`font-semibold ${
                                    conflicts[index].status === "No conflict"
                                      ? "text-green-600"
                                      : "text-red-600"
                                  }`}>
                                  {conflicts[index].status}
                                </span>
                              </p>
                            )}
                          </div>
                        ))}
                      </div>
                      {(() => {
                        const totalConflicts = conflicts.reduce(
                          (sum, conflict) =>
                            sum +
                            conflict.conflictDetails.appointments +
                            conflict.conflictDetails.meetings,
                          0
                        );
                        return totalConflicts > 0 ? (
                          <p className="mt-4 text-sm text-red-600 text-center">
                            {totalConflicts} conflicting appointments/meetings
                            detected. Booking is still permitted.
                          </p>
                        ) : (
                          <p className="mt-4 text-sm text-green-600 text-center">
                            No conflicts detected. Booking is permitted.
                          </p>
                        );
                      })()}
                    </div>
                  )}
                </div>
              </div>

              {/* Buttons */}
              <div className="mt-6 flex items-center justify-end gap-x-6">
                <button
                  type="button"
                  onClick={onClose}
                  className="text-sm font-semibold leading-6 text-gray-900">
                  Cancel
                </button>
                <button
                  type="submit"
                  className="rounded-sm bg-indigo-600 px-3.5 py-2 text-sm font-semibold text-white shadow-sm hover:bg-indigo-500 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-indigo-600">
                  {mode === "edit"
                    ? "Confirm Update Appointment"
                    : isRecurring
                    ? `Create ${recurringDates.length} Recurring Appointment${
                        recurringDates.length > 1 ? "s" : ""
                      }`
                    : isBlock
                    ? `Create ${blockDates.length} Block Appointment${
                        blockDates.length > 1 ? "s" : ""
                      }`
                    : "Create Appointment"}
                </button>
              </div>
            </form>

            {isNewClient && (
              <QuickAddClientModal onClose={() => setIsNewClient(false)} />
            )}

            {/* Confirmation Modal for out-of-hours appointments */}
            {showConfirmModal && (
              <ConfirmAppointmentModal
                isOpen={showConfirmModal}
                onClose={handleCancelConfirm}
                onConfirm={handleConfirmSave}
                warningMessage={availabilityWarning}
                appointmentDetails={{
                  date: pendingAppointmentData?.date,
                  time: pendingAppointmentData?.time,
                  clinicianName: selectedClinician ? `${selectedClinician.firstName} ${selectedClinician.lastName}` : null,
                  clientName: selectedClient ? `${selectedClient.firstName} ${selectedClient.lastName}` : null
                }}
              />
            )}
          </div>
        </div>
      </div>
    </Dialog>
  );
};

export default QuickAddAppointmentModal;
